import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, filter, share, takeUntil, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { AuthService } from './auth.service';
import { ENV } from './env.token';
import { AUTH_STORAGE_KEYS } from './multi-tenant-auth';
import { STORAGE_TOKEN } from './storage.token';
import { BROADCAST_CHANNEL_FACTORY } from './broadcast-channel.token';
import { NOW } from './now.token';
import { WINDOW } from './window.token';

const DEFAULT_SESSION_EXPIRY_MINUTES = 20;
const DEFAULT_SESSION_WARNING_MINUTES = 2;

const ACTIVITY_EVENTS = ['mousemove', 'click', 'keydown', 'touchstart', 'scroll'] as const;

/** BroadcastChannel name for sharing activity across same-origin tabs. */
const ACTIVITY_CHANNEL_NAME = 'ship:activity';

/**
 * Logs the user out after a configurable idle period, showing a warning dialog
 * `sessionWarningMinutes` beforehand. Call `start()` when authenticated, `stop()` on logout.
 *
 * Expiry is an absolute wall-clock deadline, not a live timer: browsers throttle timers in
 * background tabs, so it is re-checked at each phase boundary and on wake (visibility/focus).
 * Activity — including in other tabs via BroadcastChannel — pushes the deadline out.
 */
@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly document = inject(DOCUMENT);
  private readonly window = inject(WINDOW);
  private readonly createChannel = inject(BROADCAST_CHANNEL_FACTORY);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly env = inject(ENV);
  private readonly sessionStorage = inject(STORAGE_TOKEN);
  private readonly http = inject(HttpClient);
  private readonly now = inject(NOW);

  private readonly stop$ = new Subject<void>();
  private channel: BroadcastChannel | null = null;
  /** Single one-shot timer to the next phase boundary; reschedules itself after each fire. */
  private phaseTimer: Subscription | null = null;

  private warningMs = 0;
  private expiryMs = 0;
  /** Epoch ms when the session expires from inactivity; null when not running. */
  private expiresAt: number | null = null;

  private readonly warningCountdownSignal = signal<number | null>(null);
  /** Remaining seconds until logout during the warning phase. Null when not in warning phase. */
  readonly warningCountdown: Signal<number | null> = this.warningCountdownSignal.asReadonly();

  start(): void {
    this.cleanup(); // in case start() is called again while already running

    const channel = this.createChannel(ACTIVITY_CHANNEL_NAME);
    this.channel = channel;

    this.warningMs = (this.env.sessionWarningMinutes ?? DEFAULT_SESSION_WARNING_MINUTES) * 60_000;
    this.expiryMs = (this.env.sessionExpiryMinutes ?? DEFAULT_SESSION_EXPIRY_MINUTES) * 60_000;

    if (this.warningMs >= this.expiryMs) {
      throw new Error(
        `sessionWarningMinutes (${this.env.sessionWarningMinutes ?? DEFAULT_SESSION_WARNING_MINUTES}) must be less than sessionExpiryMinutes (${this.env.sessionExpiryMinutes ?? DEFAULT_SESSION_EXPIRY_MINUTES})`
      );
    }

    const activityEvents$ = merge(...ACTIVITY_EVENTS.map((event) => fromEvent(this.document, event, { passive: true })));
    const navigationEvents$ = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));

    // Activity resets the deadline, except during the warning phase (only "Stay Signed In" keeps
    // the session). Shared so both subscribers add a single set of DOM listeners.
    const localActivity$ = merge(activityEvents$, navigationEvents$).pipe(
      filter(() => this.warningCountdownSignal() === null),
      share()
    );

    localActivity$.pipe(takeUntil(this.stop$)).subscribe(() => {
      this.resetDeadline();
      this.scheduleNext();
    });

    // Debounced so mousemove/scroll don't saturate the channel.
    localActivity$.pipe(debounceTime(500), takeUntil(this.stop$)).subscribe(() => this.channel?.postMessage(this.expiresAt));

    fromEvent<MessageEvent>(channel, 'message')
      .pipe(takeUntil(this.stop$))
      .subscribe((event) => {
        if (this.expiresAt === null) {
          return; // already expired/stopped — don't let another tab revive it
        }
        const received = typeof event.data === 'number' ? event.data : null;
        if (received !== null) {
          this.expiresAt = Math.max(this.expiresAt, received); // adopt another tab's later deadline
        }
        this.evaluate();
        this.scheduleNext();
      });

    // Re-evaluate now on wake (visible/focus): the pending idle timer won't fire for ~18 min, so a
    // deadline that passed while hidden must be caught here, then the schedule realigned.
    const visibilityEvents$ = fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.visibilityState === 'visible'));
    const focusEvents$ = fromEvent(this.window, 'focus');

    merge(visibilityEvents$, focusEvents$)
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.evaluate();
        this.scheduleNext();
      });

    this.resetDeadline();
    this.scheduleNext();
  }

  stop(): void {
    this.cleanup();
  }

  /**
   * Resets the deadline eagerly (so the dialog closes at once) and confirms the session with the
   * backend. On failure we keep the session; a later 401 redirects if it had actually expired.
   */
  keepAlive(): Observable<void> {
    this.resetDeadline();
    this.scheduleNext();
    return this.http.post<void>('/api/auth/keep-alive', {}).pipe(
      tap(() => this.channel?.postMessage(this.expiresAt)), // sync other tabs, only on success
      catchError(() => EMPTY)
    );
  }

  /** Pushes the deadline out a full idle period and clears any active warning. */
  private resetDeadline(): void {
    this.expiresAt = this.now() + this.expiryMs;
    this.warningCountdownSignal.set(null);
  }

  /**
   * Schedules the next re-evaluation at the upcoming boundary: the warning threshold while idle,
   * then once per second (or at expiry) during the warning. Nothing to schedule once expired/stopped.
   */
  private scheduleNext(): void {
    this.phaseTimer?.unsubscribe();
    if (this.expiresAt === null) {
      return;
    }
    const remaining = this.expiresAt - this.now();
    const delay = remaining > this.warningMs ? remaining - this.warningMs : Math.max(0, Math.min(1000, remaining));
    this.phaseTimer = timer(delay)
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.evaluate();
        this.scheduleNext();
      });
  }

  /** Idle / warning / expired, from the wall-clock deadline. */
  private evaluate(): void {
    if (this.expiresAt === null) {
      return;
    }

    const remaining = this.expiresAt - this.now();

    if (remaining <= 0) {
      this.expireNow();
    } else if (remaining <= this.warningMs) {
      this.warningCountdownSignal.set(Math.ceil(remaining / 1000));
    } else {
      this.warningCountdownSignal.set(null);
    }
  }

  private expireNow(): void {
    this.expiresAt = null;
    this.warningCountdownSignal.set(null);
    this.sessionStorage.setItem(AUTH_STORAGE_KEYS.showSessionExpiredNotification, 'true');
    this.authService.logout({ showNotification: false });
  }

  private cleanup(): void {
    this.stop$.next();
    this.phaseTimer?.unsubscribe();
    this.phaseTimer = null;
    this.channel?.close();
    this.channel = null;
    this.expiresAt = null;
    this.warningCountdownSignal.set(null);
  }
}
